import { Fragment, ReactNode } from 'react';

import { Stack, Typography } from '@mui/material';

import { SxProps } from '../../types';
import { mergeSx } from '../../utils/styling';
import { HiddenElementsCounter } from '../boxes/overflow-counter/HiddenElementsCounter';
import { OverflowCounter } from '../boxes/overflow-counter/OverflowCounter';
import { OverflowItemTypography } from '../boxes/overflow-counter/OverflowItem';
import { Indicator, IndicatorProps } from '../indicators/indicator/Indicator';
import { Tooltip } from '../tooltip/Tooltip';

type Props = {
  values?: string[];
  fallback?: ReactNode;
  renderTooltip?: (props: { values: string[] }) => ReactNode;
  renderValueInTooltip?: (value: string) => ReactNode;
  countIndicatorProps?: Partial<Omit<IndicatorProps, 'count'>>;
  sx?: SxProps;
};

export const ArrayTextCell = ({
  values,
  fallback,
  renderTooltip,
  renderValueInTooltip = (value: string) => <span>{value}</span>,
  countIndicatorProps,
  sx,
}: Props) => {
  if (!values?.length) {
    return fallback;
  }

  return (
    <OverflowCounter
      visibilityThreshold={1}
      typography="inherit"
      display="flex"
      alignItems="center"
      sx={sx}
    >
      <Typography noWrap variant="inherit">
        {values.map((value, index) => (
          <OverflowItemTypography
            overflowKey={index}
            key={index}
            variant="inherit"
            component="span"
          >{`${value}${
            index < values.length - 1 ? ', ' : ''
          }`}</OverflowItemTypography>
        ))}
      </Typography>
      <HiddenElementsCounter>
        {(count) =>
          count ? (
            <Tooltip
              title={
                renderTooltip ? (
                  renderTooltip({ values })
                ) : (
                  <Stack
                    columnGap={16}
                    rowGap={4}
                    sx={
                      values.length <= 5
                        ? { display: 'flex' }
                        : {
                            display: 'grid',
                            gridTemplateColumns: `repeat(3, max-content)`,
                            gridAutoFlow: 'row',
                          }
                    }
                  >
                    {values.map((value, index) => (
                      <Fragment key={value + index}>
                        {renderValueInTooltip(value)}
                      </Fragment>
                    ))}
                  </Stack>
                )
              }
              placement="bottom"
              sx={{ maxWidth: 'unset' }}
            >
              <Indicator
                color="blue"
                size="small"
                testId="hidden-elements"
                {...countIndicatorProps}
                sx={mergeSx({ ml: 4 }, countIndicatorProps?.sx)}
                count={count}
              />
            </Tooltip>
          ) : null
        }
      </HiddenElementsCounter>
    </OverflowCounter>
  );
};
